import ClassPerformance from 'components/GradingInsights/ClassPerformance';
import GradingProgress from 'components/GradingInsights/GradingProgress';
import GradingStatistics from 'components/GradingInsights/GradingStatistics';
import { useApp } from 'contexts/app';
import { useTranslation } from 'react-i18next';

import {
    Button,
    Card,
    Container,
    Divider,
    Icon,
    Stack,
    styled,
    Tabs,
    Text,
    TextInput,
    Tooltip,
} from 'shell/components';
import { SubmissionTabs } from 'utils/constants';
import { getSubmissionGradedBy, getSubmissionGradingTime, getTooltipComponent } from 'utils/helper';

const Mobile = ({
    assignment,
    sortedFilteredSubmissions,
    getSubmissionStatus,
    getSubmissionFinalGrade,
    onClickBack,
    onClickViewAll,
    onClickViewSubmission,
    resetSubmissions,
    exportSubmissions,
    filters,
    setFilters,
    getFilterCount,
    sortState,
    activeTab,
    setActiveTab,
    handleAcceptAll,
    canAcceptAllSuggestions,
    gradingInsightsData,
}) => {
    const { accountUsers } = useApp();
    const { t } = useTranslation('assignment');

    const {
        gradingProgress,
        predictedGradingTime,
        setPredictedGradingTime,
        statisticsFilters,
        setStatisticsFilters,
        exportStatistics,
        calculatedTimeSavedPerSub,
        totalTimeSaved,
        handleCalculateTimeSave,
        handleResetTimeSave,
        sortedGraders,
        graderSortState,
        handleSetGraderSortState,
        graderColumns,
        classPerformanceStats,
        classPerformanceChartData,
        performanceFilters,
        setPerformanceFilters,
        performanceFilterOptions,
        handlePerformanceGraderFilter,
        studentGradingStatistics,
        visibleColumns,
        onColumnToggle,
        isTableExpanded,
        onToggleTableExpanded,
        studentSortState,
        handleSetStudentSortState,
        handleStatisticsGraderFilter,
        studentStatisticsColumns,
    } = gradingInsightsData;

    return (
        <Container sx={{ height: '100%' }} device={'mobile'}>
            <RootStack spacing={5}>
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {assignment.title}
                    </Text>
                </Stack>
                <Tabs
                    aria-label={'course lobby tabs'}
                    options={[
                        { key: SubmissionTabs.SUBMISSIONS, value: t('submission.submissions-label', 'Submissions') },
                        {
                            key: SubmissionTabs.GRADING_INSIGHTS,
                            value: t('submission.grading-insights-label', 'Grading Insights'),
                        },
                    ]}
                    selected={activeTab}
                    onChange={setActiveTab}
                />
                {activeTab === SubmissionTabs.SUBMISSIONS && (
                    <>
                        <Stack alignItems={'stretch'} width={'100%'} spacing={4}>
                            <Button
                                fullWidth
                                disabled={!canAcceptAllSuggestions}
                                variant={'secondary'}
                                startIcon={<Icon kind={'checkmark'} />}
                                tooltip={{
                                    component: t(
                                        'submission.accept-all-suggestions-tooltip',
                                        'Accept all grading suggestions by AI'
                                    ),
                                }}
                                onClick={handleAcceptAll}
                            >
                                {t('common:ui.accept-all', 'Accept All')}
                            </Button>
                            <Button
                                fullWidth
                                startIcon={<Icon kind={'refresh'} />}
                                variant={'tertiary'}
                                color={'destructive'}
                                onClick={resetSubmissions}
                                tooltip={{
                                    component: t(
                                        'submission.reset-grades-feedback-tooltip',
                                        'Reset grades and feedback for all submissions'
                                    ),
                                }}
                            >
                                {t('common:controls.reset', 'Reset')}
                            </Button>
                            <Button
                                fullWidth
                                startIcon={<Icon kind={'download'} />}
                                variant={'tertiary'}
                                tooltip={{
                                    component: t(
                                        'submission.export-grades-feedback-tooltip',
                                        'Export grades and/or feedback to a CSV'
                                    ),
                                }}
                                onClick={exportSubmissions}
                            >
                                {t('common:controls.export', 'Export')}
                            </Button>
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={4}>
                            <Button
                                sx={{ flexShrink: 0 }}
                                color={'neutral'}
                                startIcon={<Icon kind={'filter'} />}
                                variant={'secondary'}
                                onClick={() => setFilters((current) => ({ ...current, open: true }))}
                            >
                                ({getFilterCount(filters, sortState)})
                            </Button>
                            <TextInput
                                endIcon={<Icon kind={'search'} />}
                                placeholder={t('common:controls.search', 'Search')}
                                value={filters.search}
                                onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                            />
                        </Stack>
                        <Stack divider={<Divider />} spacing={3}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text color={'light'}>{t('grades-feedback', 'Grades/Feedback')}</Text>
                                <Button
                                    disabled={sortedFilteredSubmissions.every((submission) => !submission.grading)}
                                    variant={'borderless'}
                                    onClick={onClickViewAll}
                                >
                                    {t('common:controls.view-all', 'View All')}
                                </Button>
                            </Stack>
                            <Stack spacing={5}>
                                {sortedFilteredSubmissions.map((submission) => (
                                    <Card key={submission.id}>
                                        <Stack sx={{ minWidth: 0, width: '100%' }} divider={<Divider />} spacing={2}>
                                            <Stack
                                                alignItems={'flex-start'}
                                                direction={'row'}
                                                justifyContent={'space-between'}
                                                spacing={2}
                                            >
                                                <Text
                                                    sx={{ minWidth: 0 }}
                                                    component={'p'}
                                                    variant={'h6'}
                                                    weight={'semibold'}
                                                >
                                                    {submission.files[0].original_file
                                                        ? submission.files[0].original_file.name
                                                        : submission.files[0].name}
                                                </Text>
                                                <Button
                                                    disabled={!submission.grading}
                                                    variant={'borderless'}
                                                    onClick={() => onClickViewSubmission(submission)}
                                                >
                                                    {t('common:controls.view', 'View')}
                                                </Button>
                                            </Stack>
                                            <Stack spacing={2}>
                                                <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                                    <Text color={'light'}>Status:</Text>
                                                    {getSubmissionStatus(submission)}
                                                </Stack>
                                                {assignment.ai_grading_enabled && (
                                                    <Text color={'light'}>
                                                        {t('common:table.final-grade', 'Final Grade')}:{' '}
                                                        {getSubmissionFinalGrade(submission)}
                                                    </Text>
                                                )}
                                                <Stack
                                                    sx={{ height: '16px' }}
                                                    alignItems={'center'}
                                                    direction={'row'}
                                                    spacing={1}
                                                >
                                                    <Text color={'light'}>Grading Time (mm:ss)</Text>
                                                    <Tooltip
                                                        content={getTooltipComponent(
                                                            t(
                                                                'submission.how-is-grading-time-measured',
                                                                'How is grading time measured?'
                                                            ),
                                                            t(
                                                                'submission.how-is-grading-time-measured-description',
                                                                'Grading time is tracked only while you are active on the grading page. If you switch to another window or tab, tracking pauses. This metric may not accurately reflect grading time, as interruptions or background activity can affect tracking. Use it as an estimate alongside other data.'
                                                            )
                                                        )}
                                                        type={'info'}
                                                    />
                                                    <Text color={'light'}>
                                                        : {getSubmissionGradingTime(submission)}
                                                    </Text>
                                                </Stack>
                                                <Text color={'light'}>
                                                    {t('common:table.graded-by', 'Graded By')}:{' '}
                                                    {getSubmissionGradedBy(submission, accountUsers)}
                                                </Text>
                                            </Stack>
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        </Stack>
                    </>
                )}
                {activeTab === SubmissionTabs.GRADING_INSIGHTS && (
                    <Stack spacing={5} sx={{ pb: '3rem' }}>
                        <Stack alignItems={'flex-start'} direction={'column'} spacing={2.5}>
                            <SubHeading variant={'h2'} weight={'medium'}>
                                {t('submission.grading-insights-label', 'Grading Insights')}
                            </SubHeading>
                            <Button
                                fullWidth
                                startIcon={<Icon kind={'download'} />}
                                variant={'secondary'}
                                onClick={gradingInsightsData.exportGraderStatistics}
                            >
                                {t('common:controls.export-to-csv', 'Export to CSV')}
                            </Button>
                        </Stack>
                        <GradingProgress
                            device={'mobile'}
                            gradingProgress={gradingProgress}
                            predictedGradingTime={predictedGradingTime}
                            setPredictedGradingTime={setPredictedGradingTime}
                            calculatedTimeSavedPerSub={calculatedTimeSavedPerSub}
                            totalTimeSaved={totalTimeSaved}
                            handleCalculateTimeSave={handleCalculateTimeSave}
                            handleResetTimeSave={handleResetTimeSave}
                            sortedGraders={sortedGraders}
                            graderSortState={graderSortState}
                            handleSetGraderSortState={handleSetGraderSortState}
                            columns={graderColumns}
                        />
                        <ClassPerformance
                            device={'mobile'}
                            assignment={assignment}
                            submissions={sortedFilteredSubmissions}
                            classPerformanceStats={classPerformanceStats}
                            classPerformanceChartData={classPerformanceChartData}
                            performanceFilters={performanceFilters}
                            setPerformanceFilters={setPerformanceFilters}
                            filterOptions={performanceFilterOptions}
                            onGraderFilterChange={handlePerformanceGraderFilter}
                        />
                        <GradingStatistics
                            device={'mobile'}
                            assignment={assignment}
                            submissions={sortedFilteredSubmissions}
                            statisticsFilters={statisticsFilters}
                            setStatisticsFilters={setStatisticsFilters}
                            onGraderFilterChange={handleStatisticsGraderFilter}
                            exportStatistics={exportStatistics}
                            filterOptions={performanceFilterOptions}
                            tableData={studentGradingStatistics}
                            visibleColumns={visibleColumns}
                            onColumnToggle={onColumnToggle}
                            isTableExpanded={isTableExpanded}
                            onToggleTableExpanded={onToggleTableExpanded}
                            sortState={studentSortState}
                            handleSortState={handleSetStudentSortState}
                            columns={studentStatisticsColumns}
                        />
                    </Stack>
                )}
            </RootStack>
        </Container>
    );
};

const RootStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        paddingBottom: theme.spacing(5),
    };
});

const SubHeading = styled(Text)(() => {
    return {
        fontSize: '1.125rem',
        lineHeight: '1.5rem',
    };
});

export default Mobile;
